<template>
  <div>
    <v-data-table
      :headers="headers"
      :items="enrollments"
      class="elevation-1 mt-2"
      dense
      disable-pagination
      hide-default-footer
      no-data-text="No setup codes are waiting to be used."
    >
      <template v-slot:top>
        <v-toolbar flat>
          <v-toolbar-title>Workstation Setup</v-toolbar-title>
          <v-divider class="mx-4" inset vertical></v-divider>
          <v-checkbox
            id="enrollmentShowFinished"
            v-model="showFinished"
            label="Show used and cancelled"
            dense
            hide-details
            class="mt-4"
            @change="reload"
          ></v-checkbox>
          <v-spacer></v-spacer>
          <v-btn
            id="enrollmentNewBtn"
            small
            color="primary"
            @click="enrollDialog = true"
          >
            Set Up a Workstation
          </v-btn>
        </v-toolbar>
      </template>

      <template v-slot:[`item.scopes`]="{ item }">
        <span>{{ (item.scopes || []).join(', ') || '&mdash;' }}</span>
      </template>
      <template v-slot:[`item.wkst`]="{ item }">
        <span>{{ item.wkst || '—' }}</span>
      </template>
      <template v-slot:[`item.createdAt`]="{ item }">
        <span>{{ shortDate(item.createdAt) }}</span>
      </template>
      <template v-slot:[`item.expiresAt`]="{ item }">
        <span>{{ shortDate(item.expiresAt) }}</span>
      </template>
      <template v-slot:[`item.status`]="{ item }">
        <span :class="statusClass(item)">{{ statusLabel(item) }}</span>
      </template>

      <template v-slot:[`item.actions`]="{ item }">
        <v-icon
          v-if="item.status === 'pending'"
          small
          class="mr-2 machine-enrollment-cancel"
          title="Cancel this code so it cannot set up a workstation"
          @click="cancel(item)"
        >
          {{ icons.mdiCancel }}
        </v-icon>
      </template>
    </v-data-table>

    <MachineTokenEnrollDialog v-model="enrollDialog" @input="onDialogClosed" />
  </div>
</template>

<script>
import { mdiCancel } from '@mdi/js';
import MachineTokenEnrollDialog from './MachineTokenEnrollDialog.vue';

/**
 * Codes issued but not yet used, which is the half of enrollment nothing else can show.
 *
 * Without this list, "I set that desk up yesterday and nothing happened" has no answer: a code that
 * was never redeemed looks exactly like one that was, and a code claimed by the wrong machine looks
 * like nothing at all. `claimedFrom` and the token it became are here for the second case.
 *
 * Cancel is offered only while a code is pending. Once it has been claimed the desk holds a real
 * token, and cancelling the code would not stop it - the server refuses, and this only shows the
 * action where it can succeed. That is the same rule the token list follows about delete.
 */
export default {
  name: 'MachineEnrollmentsList',
  components: {
    MachineTokenEnrollDialog,
  },
  data: () => ({
    icons: { mdiCancel },
    enrollDialog: false,
    showFinished: false,
    headers: [
      { text: 'Label', value: 'label' },
      { text: 'Scopes', value: 'scopes' },
      { text: 'wkst #', value: 'wkst' },
      { text: 'Issued', value: 'createdAt' },
      { text: 'Good until', value: 'expiresAt' },
      { text: 'Status', value: 'status', sortable: false },
      { text: 'Action', value: 'actions', sortable: false },
    ],
  }),
  computed: {
    enrollments() {
      return this.$store.state.UserAdministration.machineTokenEnrollments || [];
    },
  },
  created() {
    this.reload();
  },
  methods: {
    reload() {
      this.$store.dispatch('UserAdministration/loadMachineTokenEnrollments', {
        includeClaimed: this.showFinished,
        includeCanceled: this.showFinished,
      });
    },
    onDialogClosed(isOpen) {
      // A code issued from the dialog is already in the list, but a desk may have redeemed one
      // while the dialog was open, and the token list next door will have changed too.
      if (!isOpen) {
        this.reload();
        this.$store.dispatch('UserAdministration/loadMachineTokens', { includeRevoked: true });
      }
    },
    shortDate(value) {
      if (!value) return '';
      const parsed = new Date(value);
      if (Number.isNaN(parsed.getTime())) return String(value);
      return parsed.toLocaleString();
    },
    statusLabel(item) {
      return {
        pending: 'Waiting to be used',
        claimed: 'Used',
        expired: 'Expired',
        canceled: 'Cancelled',
      }[item.status] || item.status;
    },
    statusClass(item) {
      if (item.status === 'pending') return 'green--text';
      if (item.status === 'claimed') return 'grey--text';
      return 'orange--text';
    },
    async cancel(item) {
      const name = item.label || item.enrollmentId;
      const message = `Cancel the setup code for "${name}"? Whoever has it will not be able to set `
        + 'up that workstation, and you will need to issue a new one.';
      if (!window.confirm(message)) return;
      await this.$store.dispatch('UserAdministration/cancelMachineTokenEnrollment', {
        enrollmentId: item.enrollmentId,
      });
    },
  },
};
</script>
